/**
 * "Classic" scenes — the 14 hand-authored gallery room shapes from the
 * legacy pattern/renderer, surfaced inside the new studio's SCENE section
 * as an alternative to the procedural presets in constants.ts.
 *
 * Source data is `TemplateSets.GalleryTemplates` (src/utils/pattern-assets.ts):
 * 56 flat entries, ids 400–455, 14 room families × 4 print-density variants
 * each (1 / 6 / 18 / 5(or 6/3) large or small objects — see
 * src/features/gallery-wizard/galleryMeshCatalog.ts's GALLERY_PRINT_MESHES
 * for the authoritative per-id slot count). This module regroups that flat
 * list by family so the picker shows 14 rooms instead of a 56-item wall of
 * text, and can pick the density variant that best matches the user's
 * current print count.
 */
import { Assets, TemplateSets } from '@/utils/pattern-assets'
import { getGallerySlotCount } from '@/features/gallery-wizard/galleryMeshCatalog'
import { ClassicGlyphProps } from '@/design-system'

const ASSET_ROOT = '/assets/patterns/'

export interface ClassicVariant {
  id: number
  name: string
  thumbUrl: string
  /** Relative to ASSET_ROOT, e.g. "templates/meshes/GalleryScene_001_002.babylon" */
  meshPath: string
  printCount: number
  radius?: number
  radiusMax?: number
}

export interface ClassicFamily {
  /** 1-based family index, matches the "Gallery 0NN" naming. */
  family: number
  /** Friendly display name — "Gallery 001" read oddly outside the old wizard's context. */
  label: string
  variants: ClassicVariant[]
}

function toAssetUrl(path?: string): string {
  if (!path) return ''
  if (path.startsWith('http') || path.startsWith('/')) return path
  return `${ASSET_ROOT}${path}`
}

/** Parses the "Gallery 001" family number out of "Gallery 001 - 6 Small Objects". */
function parseFamily(name: string): number {
  const m = /Gallery\s+(\d+)/i.exec(name)
  return m ? parseInt(m[1], 10) : 0
}

/**
 * Real display names for the 14 families — "Classic 01"–"Classic 14" only
 * ever meant something inside the old flat 56-item wizard list; grouped by
 * room here, that numbering is meaningless to anyone picking a look. Each
 * name is grounded in that family's actual wall/floor/motif from
 * `CLASSIC_GLYPHS` below (e.g. family 1's warm orange + drape motif → "The
 * Drape Room"), not an arbitrary label. See docs/mockups-v22/
 * 04-sidebar-scene-picker-remock.html for the reference grid this was
 * drafted against.
 */
export const CLASSIC_FAMILY_NAMES: Record<number, string> = {
  1: 'The Drape Room',
  2: 'The Fluted Hall',
  3: 'Dark Timber',
  4: 'The Tin Loft',
  5: 'Slate & Rust',
  6: 'The Foyer',
  7: 'The Marquee',
  8: 'Salon Rouge',
  9: 'The Screening Room',
  10: 'The Beam Loft',
  11: 'Black Mirror Floor',
  12: 'The Archway',
  13: 'The Colonnade',
  14: 'Raw Concrete',
}

export const CLASSIC_FAMILIES: ClassicFamily[] = (() => {
  const byFamily = new Map<number, ClassicVariant[]>()
  for (const t of TemplateSets.GalleryTemplates || []) {
    const family = parseFamily(t.name)
    if (!family) continue
    const variant: ClassicVariant = {
      id: t.id,
      name: t.name,
      thumbUrl: toAssetUrl(t.thumb),
      meshPath: t.url,
      printCount: getGallerySlotCount(t.id),
      radius: t.radius,
      radiusMax: t.radiusMax,
    }
    const list = byFamily.get(family) || []
    list.push(variant)
    byFamily.set(family, list)
  }
  return Array.from(byFamily.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([family, variants]) => ({
      family,
      label: CLASSIC_FAMILY_NAMES[family] || `Classic ${String(family).padStart(2, '0')}`,
      variants: variants.sort((a, b) => a.printCount - b.printCount),
    }))
})()

/**
 * Cover glyph for each Classic family — the 14 legacy shells are mostly the
 * same corner box with a different wall/floor material or a single
 * architectural feature layered in, so each entry captures the room's real
 * palette plus its one distinguishing trait rather than a literal (and, at
 * the 40px picker size, illegible) screenshot. See `ClassicGlyph`.
 */
export const CLASSIC_GLYPHS: Record<number, Omit<ClassicGlyphProps, 'size' | 'className'>> = {
  1: { wallColor: '#e8792a', floorColor: '#f4e9dc', motif: 'drape' },
  2: { wallColor: '#d8cdbc', floorColor: '#c9bca6', motif: 'flute' },
  3: { wallColor: '#4a2e1e', floorColor: '#e9e4da', motif: 'plank' },
  4: { wallColor: '#4b3040', floorColor: '#2b1f26', motif: 'corrugated', motifColor: '#ece6dd' },
  5: { wallColor: '#4a4a4a', wallColor2: '#8b3a2b', floorColor: '#c9bfaf', motif: 'colorblock' },
  6: { wallColor: '#f1ede6', floorColor: '#b98d5b', motif: 'plain' },
  7: { wallColor: '#b7a99a', floorColor: '#c99a62', motif: 'marquee', motifColor: '#1a1a1a' },
  8: { wallColor: '#f1ede6', wallColor2: '#7a2e28', floorColor: '#b98d5b', motif: 'colorblock' },
  9: { wallColor: '#3e5c46', floorColor: '#2b3e31', motif: 'felt' },
  10: { wallColor: '#edeae3', floorColor: '#d8d2c6', motif: 'beam', motifColor: '#2a2a2a' },
  11: { wallColor: '#e7e7e7', floorColor: '#0f0f10', motif: 'glossyFloor' },
  12: { wallColor: '#f1ede6', wallColor2: '#5b3a2e', floorColor: '#b98d5b', motif: 'archway' },
  13: { wallColor: '#4a5568', floorColor: '#3b2a1e', motif: 'columns' },
  14: { wallColor: '#9b9c98', floorColor: '#868782', motif: 'concrete' },
}

export function getClassicGlyph(family: number): Omit<ClassicGlyphProps, 'size' | 'className'> {
  return CLASSIC_GLYPHS[family] || { wallColor: '#3a3944', floorColor: '#221f28', motif: 'plain' }
}

export function getClassicFamily(family: number): ClassicFamily | undefined {
  return CLASSIC_FAMILIES.find((f) => f.family === family)
}

export function getClassicVariant(id: number): ClassicVariant | undefined {
  for (const f of CLASSIC_FAMILIES) {
    const v = f.variants.find((variant) => variant.id === id)
    if (v) return v
  }
  return undefined
}

/** The family a given template id belongs to, or undefined if not a Classic id. */
export function getClassicFamilyForId(id: number): ClassicFamily | undefined {
  return CLASSIC_FAMILIES.find((f) => f.variants.some((v) => v.id === id))
}

/**
 * Full slot range for *browsing* — `pickBestVariant` below intentionally
 * collapses to each family's smallest variant whenever the room has few
 * photos, which is correct for auto-fitting on click but wrong for a label
 * meant to convey "how big can this room get" while comparing all 14 side
 * by side. Every family's variants happen to span the same 1–18 range, so
 * this reads identically across cards — that's the real shape of the data,
 * not a picker bug.
 */
export function getClassicSlotRange(family: ClassicFamily): { min: number; max: number } {
  const counts = family.variants.map((v) => v.printCount)
  return { min: Math.min(...counts), max: Math.max(...counts) }
}

/** Best-fit density variant within a family for a given print count — closest match, ties favor more slots (never crops fewer prints than necessary). */
export function pickBestVariant(family: ClassicFamily, printCount: number): ClassicVariant {
  let best = family.variants[0]
  let bestScore = Infinity
  for (const v of family.variants) {
    const score = v.printCount >= printCount ? v.printCount - printCount : (printCount - v.printCount) + 1000
    if (score < bestScore) {
      bestScore = score
      best = v
    }
  }
  return best
}

/**
 * Default Classic family for freshly-created galleries (task #17 — Classic
 * first for launch, Studio's corridor/loft/pavilion hidden behind
 * `RoomInspector`'s mode tabs until phase 2). Classic 06 — plain cream
 * walls, no motif — is the most neutral, universally-safe look of the 14.
 */
export const DEFAULT_LAUNCH_CLASSIC_FAMILY = 6

/** Best-fit Classic template for a brand-new seed, sized to `printCount`. */
export function pickLaunchClassicTemplate(printCount: number): number {
  const family = getClassicFamily(DEFAULT_LAUNCH_CLASSIC_FAMILY) || CLASSIC_FAMILIES[0]
  return pickBestVariant(family, printCount).id
}

/**
 * Real frame models. `Assets.frames` (src/utils/pattern-assets.ts) is a
 * single mesh per style, which is what the legacy renderer's own
 * `applyGalleryFrame`/`adjustFrameScale` (src/pages/pattern/renderer/main.js)
 * non-uniformly scales to fit each print slot's bounding box — fine for a
 * plain rectangular border, but any style with baked mitred/beveled corner
 * detail (e.g. Frame 007's gold bevel, Frame 008's double-ruled mat) gets
 * that corner geometry stretched into a visibly skewed diagonal on any
 * non-square slot, since `scaling.x`/`scaling.z` distorts the whole mesh
 * including its corners.
 *
 * `Assets.patternFrames` ships a 9-sliced version of every one of these same
 * styles (`Frame_Back` + `Frame_Side{T,B,L,R}` + `Frame_Corner{TL,TR,BL,BR}`
 * — same ids as `Assets.frames`) authored for the legacy pattern editor's
 * adjustable-matting frame (`applyPatternFrame`/`adjustPatternFrameScale`),
 * where corners are only ever translated (never scaled) and the straight
 * sides stretch along their own length axis only. `applyFrames` in
 * `useStudioLegacyRoom` uses that instead — it fits any print aspect ratio
 * without distorting the corner detail, which the legacy classic-gallery
 * path never did for this per-print case.
 */
export interface ClassicFrameOption {
  id: number
  name: string
  thumbUrl: string
  /** Relative to ASSET_ROOT, e.g. "frames/meshes/Frame_001.babylon" — single-mesh fallback, only used if `patternMeshPath` is missing. */
  meshPath: string
  /** Relative to ASSET_ROOT, e.g. "patternFrame/meshes/Frame_001.babylon" — 9-sliced corner-preserving mesh, preferred. */
  patternMeshPath?: string
}

const PATTERN_FRAME_MESH_BY_ID = new Map((Assets.patternFrames || []).map((f) => [f.id, f.url]))

export const CLASSIC_FRAME_OPTIONS: ClassicFrameOption[] = (Assets.frames || []).map((f) => ({
  id: f.id,
  name: f.name,
  thumbUrl: toAssetUrl(f.thumb),
  meshPath: f.url,
  patternMeshPath: PATTERN_FRAME_MESH_BY_ID.get(f.id),
}))

/** Fallback for a print that's never had a frame explicitly chosen — keeps every Classic slot dressed by default instead of showing bare photos. */
export const DEFAULT_CLASSIC_FRAME_ID: number | undefined = CLASSIC_FRAME_OPTIONS[0]?.id

export function getClassicFrameOption(id: number | null | undefined): ClassicFrameOption | undefined {
  if (id == null) return undefined
  return CLASSIC_FRAME_OPTIONS.find((f) => f.id === id)
}

/**
 * Fallback mat width for a print that's never had one explicitly set — a
 * fraction of the print slot's own (shorter) side, not meters, since Classic
 * slots span a wide range of physical sizes across the 14 room shapes. Mirrors
 * `DEFAULT_CLASSIC_FRAME_ID`'s "always dressed by default" intent; 0.08 reads
 * as "8%" in the inspector's slider, matching the legacy wizard's matting copy.
 */
export const DEFAULT_CLASSIC_MAT_W = 0.08
